/**
 * Seeded PRNG using mulberry32. Returns a function that yields uniformly
 * distributed floats in [0, 1).
 *
 * Why: procgen, wave generation, perk selection, and breeding must all be
 * reproducible from a seed so we can replay runs, share seeds, and test
 * deterministically.
 */
export function createRng(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Convenience: random integer in [min, max] inclusive. Precondition: min <= max. */
export function randInt(rng: () => number, min: number, max: number): number {
  return Math.floor(rng() * (max - min + 1)) + min;
}

/** Convenience: pick one element uniformly at random. */
export function pick<T>(rng: () => number, arr: readonly T[]): T {
  if (arr.length === 0) throw new Error('pick: empty array');
  return arr[Math.floor(rng() * arr.length)]!;
}

/** Convenience: shuffle a copy of an array (Fisher-Yates). */
export function shuffle<T>(rng: () => number, arr: readonly T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}
